import { HTMLAttributes, useState } from 'react';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { PolicyHandleSourceReNew } from '@/dashboard/types/PolicyTypes';
import { getRenewPolicyPath } from '@/dashboard/utils/routes';
import { RefreshIcon } from '@heroicons/react/outline';
import { JoveLogoLoading } from '@jove-frontend/mui';
import { apiGetThePolicyInfoOfRealOperation, formatCommonDateStr } from '@jove-frontend/uikit';
import { BorderColorOutlined, CheckCircle, ExpandLessOutlined, ExpandMoreOutlined, LibraryAddOutlined } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import PolicyProductCoverageAndPriceInfoView, { PolicyCoverageAndPriceViewProps } from './components/coverAndPrice';

type RenewPolicyProductInfoViewProps = HTMLAttributes<HTMLDivElement> &
  PolicyCoverageAndPriceViewProps & {
    renewQuotePayload?: any;
    dataSourceType?: 'confirmed' | 'draft';
    paymentScheduleList?: any[];
    policy?: PolicyModel;
  };
const RenewPolicyProductInfoView = (props: RenewPolicyProductInfoViewProps) => {
  const { className, coverList, priceList, renewQuotePayload, dataSourceType, paymentScheduleList, policy } = props;
  const { push } = useProjectNavigate();

  const [expand, setExpand] = useState(true);
  const [loading, setLoading] = useState(false);

  /**
   * requests
   */
  const requestRealOperationPolicyInfo = async (): Promise<any> => {
    setLoading(true);
    const [res] = await apiGetThePolicyInfoOfRealOperation({
      policyId: policy?.id,
      handleType: 'renew',
    });
    setLoading(false);

    return res;
  };

  /**
   * methods
   */
  const expandAction = () => {
    setExpand(!expand);
  };

  const renewAction = async () => {
    const res = await requestRealOperationPolicyInfo();

    if (res) {
      push({
        path: getRenewPolicyPath(res.id, {
          newDetail: true,
          detailSource: PolicyHandleSourceReNew,
        }),
      });
    }
  };

  return (
    <Box className={clsx('border-r-grey-10 relative flex flex-col rounded-xl border', className)}>
      {loading && <JoveLogoLoading />}
      <Box className="flex flex-row justify-between px-3 py-2">
        <Box className="flex flex-row items-center space-x-2">
          {!dataSourceType && <LibraryAddOutlined />}
          {dataSourceType === 'confirmed' && (
            <Box className="flex flex-row items-center space-x-2 rounded-lg border border-dashed border-[#0E35D1] px-2 py-1 text-[#0E35D1]">
              <CheckCircle className="h-5 w-5" />
              <Typography variant="subtitle2" className="font-bold">
                Confirmed
              </Typography>
            </Box>
          )}
          {dataSourceType === 'draft' && (
            <Box className="flex flex-row items-center space-x-2 rounded-lg border border-dashed border-[#F9AB01] px-2 py-1 text-[#F9AB01]">
              <BorderColorOutlined className="h-5 w-5" />
              <Typography variant="subtitle2" className="font-bold">
                Draft
              </Typography>
            </Box>
          )}
          <Typography variant="subtitle1" className="font-bold">
            Renew policy info
            {dataSourceType === 'confirmed' && !!renewQuotePayload && (
              <span className="ml-3 text-sm font-medium text-[#FF9F1C]">
                {formatCommonDateStr(renewQuotePayload.projectPeriodStart) + ' - ' + formatCommonDateStr(renewQuotePayload.projectPeriodEnd)}
              </span>
            )}
          </Typography>
          {policy?.canRenew && dataSourceType === 'draft' && (
            <Button
              className="text-brand-primary ml-1 text-sm font-bold"
              variant="text"
              startIcon={<RefreshIcon className="h-5 w-5" />}
              onClick={renewAction}
            >
              Update policy renew
            </Button>
          )}
        </Box>
        <IconButton onClick={expandAction}>{expand ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}</IconButton>
      </Box>
      {expand && (
        <>
          <Divider />
          <Box className="flex flex-col space-y-2 p-3">
            <PolicyProductCoverageAndPriceInfoView
              coverList={coverList}
              priceList={priceList}
              paymentScheduleList={paymentScheduleList}
              hidePaymentScheduleColumnList={['paymentTime']}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default RenewPolicyProductInfoView;
